// @ts-nocheck
import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import { getStartDate } from 'support/mediaTileHelper';
import { parseDate } from 'common/support/dateHelper';

import { Brand } from 'models/propTypes/tile';
import AuthNWrapper from 'components/hoc/AuthNWrapper';
import EventTitle from 'components/common/tile/EventTitle';
import { useNetworkName } from 'hooks/kitchenSink';

import MediaTile from './MediaTile';
import BrandedAuthTile from './BrandedAuthTile';
import styles from './LiveTile.module.scss';

const messages = {
  authText: {
    id: 'tile.auth.text',
    description: 'Auth text for Media Tile',
  },
  liveFlag: {
    id: 'tile.liveFlag',
    description: 'Text for live flag',
  },
  livePregameFlag: {
    id: 'tile.livePregameFlag',
    description: 'Text for live pregame flag',
  },
  livePostgameFlag: {
    id: 'tile.livePostgameFlag',
    description: 'Text for live postgame flag',
  },
  nowPlaying: {
    id: 'tile.nowPlaying',
    description: 'Text for now playing video flag',
  },
  ttsLive: {
    id: 'tile.tts.live',
    description: 'live',
  },
  ttsLivePregame: {
    id: 'tile.tts.livePregame',
    description: 'live pregame',
  },
  ttsLivePostgame: {
    id: 'tile.tts.livePostgame',
    description: 'live postgame',
  },
  gameAt: {
    id: 'tile.gameAt',
    description: 'Text for game at',
  },
};

const LiveTile = ({
  prefix,
  title,
  brand,
  authenticated,
  authTypes,
  isNowPlaying,
  status,
  startDate,
  ...rest
}) => {
  const intl = useIntl();
  const isPregame = status === 'pre';
  const isPostgame = status === 'post';
  const dateString = getStartDate(intl, parseDate(startDate));

  const getLiveBadgeTTS = () => {
    if (isPregame) {
      return intl.formatMessage(messages.ttsLivePregame);
    }
    if (isPostgame) {
      return intl.formatMessage(messages.ttsLivePostgame);
    }
    return intl.formatMessage(messages.ttsLive);
  };

  const tts = [
    isNowPlaying ? intl.formatMessage(messages.nowPlaying) : null,
    !authenticated ? intl.formatMessage(messages.authText) : null,
    getLiveBadgeTTS(),
    isPregame ? intl.formatMessage(messages.gameAt) : null,
    isPregame ? dateString : null,
    title,
    useNetworkName(brand),
  ]
    .filter(text => text)
    .join(', ');

  const renderTitle = hasFocus => (
    <EventTitle
      prefix={prefix}
      title={title}
      brand={brand}
      shouldScroll={hasFocus}
    />
  );

  const getBadgeText = () => {
    if (isPregame) {
      return intl.formatMessage(messages.livePregameFlag);
    }
    if (isPostgame) {
      return intl.formatMessage(messages.livePostgameFlag);
    }
    return intl.formatMessage(messages.liveFlag);
  };

  const renderMetadata = hasFocus => (
    <React.Fragment>
      <BrandedAuthTile
        hasFocus={hasFocus}
        brand={brand}
        authenticated={authenticated}
        authTypes={authTypes}
      />
      <div className={classNames('d-flex flex-row w-100', styles.badgeRow)}>
        <span className={styles.liveBadge} data-testid="live-badge">
          {getBadgeText()}
        </span>
        {isPregame && (
          <span className={styles.gameTime}>
            <span className={styles.staticText}>
              {intl.formatMessage(messages.gameAt)}
            </span>
            <span className={styles.startDate}>{dateString}</span>
          </span>
        )}
      </div>
    </React.Fragment>
  );

  return (
    <MediaTile
      renderTitle={renderTitle}
      renderMetadata={renderMetadata}
      isLive
      isNowPlaying={isNowPlaying}
      ttsText={tts}
      {...rest}
    />
  );
};

LiveTile.propTypes = {
  prefix: PropTypes.string,
  title: PropTypes.string.isRequired,
  brand: Brand,
  authenticated: PropTypes.bool,
  authTypes: PropTypes.arrayOf(PropTypes.string),
  isNowPlaying: PropTypes.bool,
  startDate: PropTypes.number,
  status: PropTypes.string.isRequired,
};

LiveTile.defaultProps = {
  prefix: null,
  authenticated: false,
  authTypes: [],
  brand: null,
  isNowPlaying: false,
  startDate: null,
};

export default React.memo(AuthNWrapper(LiveTile));
